import { FormField, FormItem, FormLabel, FormControl } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { objectId } from "@/src/lib/object-id";
import { TrashIcon, PlusIcon, Info } from "lucide-react";
import { Dispatch, FocusEvent, Fragment, SetStateAction, useEffect, useState } from "react";
import { OfferYachtForm } from "../offer-yacht.schema";
import { Button } from "@/src/components/ui/button";
import { useFormContext, useWatch } from "react-hook-form";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/ui/tooltip";
import { YachtPrice } from "./ChangeYachtDetailsModal";
import { addDays } from "date-fns";
import { Checkbox } from "@/src/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { Separator } from "@/src/components/ui/separator";
import { Pricing } from "./Pricing";
import { Amenity, YachtDetails } from "@/src/app/Yachts/model";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { DatePicker } from "@/src/components/ui/date-picker";
import { differenceInDays, format, startOfDay, subDays } from "date-fns";
import { logOfferYachtControlChange, OfferYachtLogContext, useOfferCreatorBlurLogger } from "../../offerCreatorLogs";
export type PaymentScheduleProps = {
  acceptCardPayments: boolean;
  setAcceptCardPayments: Dispatch<SetStateAction<boolean>>;
  yacht: YachtDetails;
  amenities: Amenity[];
  price: YachtPrice | null;
  offerCurrencyCode: string;
  isLoading: boolean;
  logContext: OfferYachtLogContext;
};
export function PaymentSchedule({ acceptCardPayments, setAcceptCardPayments, ...scheduleProps }: PaymentScheduleProps) {
  const { captureFieldFocus, logOfferYachtControlBlur } = useOfferCreatorBlurLogger();
  const [dates, setDates] = useState<string[]>([]);
  const form = useFormContext<OfferYachtForm>();
  const price = scheduleProps.price;
  const paymentSchedule = form.watch("paymentSteps");
  const paymentScheduleWatch = useWatch({
    control: form.control,
    name: "paymentSteps",
  });
  const bookingDate = form.watch("startDate");

  useEffect(() => {
    if (paymentScheduleWatch && bookingDate) {
      const dates = paymentScheduleWatch?.map((ps) => {
        const days = ps.days;
        if (days !== null) {
          const bd = startOfDay(new Date(bookingDate));
          const newDueDate = subDays(bd, days);
          return format(newDueDate, "yyyy-MM-dd");
        } else {
          return "";
        }
      });
      !!dates?.length ? setDates(dates) : setDates([]);
    }
  }, [paymentScheduleWatch, bookingDate]);

  const getDateTooltipText = (index: number) => {
    const step = paymentSchedule[index];
    let date;
    if (step.type === "before booking date") {
      date = addDays(bookingDate, -step.days);
      if (!bookingDate) return "Choose booking end date first";
    } else if (step.type === "after confirmation") {
      date = addDays(new Date(), step.days);
    } else {
      date = new Date();
    }

    date = date.toLocaleDateString("en-EN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    return `${index + 1} payment due date is ${date}`;
  };

  const blockScroll = (e: FocusEvent<HTMLInputElement>) =>
    e.target?.addEventListener(
      "wheel",
      function (e) {
        e.preventDefault();
      },
      { passive: false },
    );

  const normalizeDate = (dateString: string): Date => {
    const parsedDate = new Date(dateString);

    if (isNaN(parsedDate.getTime())) {
      throw new Error("Invalid date");
    }

    return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
  };
  const countDaysBeforeBooking = (dueDate: Date) => {
    const bd = startOfDay(new Date(bookingDate));
    const dd = startOfDay(new Date(dueDate));
    const diff = differenceInDays(dd, bd);
    return Math.abs(diff);
  };

  return (
    <div>
      <h4 className="text-xl font-semibold mb-4">Change the payment schedule</h4>
      <div className="flex items-center gap-x-2 mb-2">
        <Checkbox
          className="w-6 h-6"
          checked={acceptCardPayments}
          onClick={() => {
            setAcceptCardPayments((prevState) => {
              const next = !prevState;
              logOfferYachtControlChange({
                eventType: "offer_yacht.payment_schedule_changed",
                context: scheduleProps.logContext,
                field: "cardPayments",
                before: prevState,
                after: next,
              });
              form.setValue("cardPayments", next);
              return next;
            });
          }}
        />
        <p>Accept card payments</p>
      </div>
      <div className="flex flex-col gap-4">
        {paymentSchedule?.map((step, i) => (
          <Fragment key={objectId(step)}>
            <div className="flex gap-2 items-end w-full">
              <FormField<OfferYachtForm, `paymentSteps.${number}.amount`>
                name={`paymentSteps.${i}.amount`}
                render={({ field: { value, onChange, ...props } }) => (
                  <FormItem className="flex-[1]" required>
                    <FormLabel className="flex align-center">
                      Amount {i + 1}{" "}
                      {price?.clientPrice && (
                        <InfoTooltip
                          text={`${i + 1} estimated payment ${renderPrice((price.clientPrice * value) / 100)} ${price.currency}`}
                        />
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...props}
                        onFocus={(e) => {
                          blockScroll(e);
                          captureFieldFocus(`paymentSteps.${i}.amount`, value);
                        }}
                        value={step.amount ?? ""}
                        type="number"
                        step="0.01"
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "") {
                            onChange(undefined);
                            return;
                          }
                          const parsed = parseFloat(val);
                          if (!isNaN(parsed)) {
                            onChange(parsed);
                          }
                        }}
                        onBlur={(e) => {
                          props.onBlur?.();
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.payment_schedule_changed",
                            context: scheduleProps.logContext,
                            key: `paymentSteps.${i}.amount`,
                            field: `paymentSteps.${i}.amount`,
                            before: value,
                            after: form.getValues(`paymentSteps.${i}.amount`),
                            meta: { step_index: i },
                          });
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex-[2]">
                <DatePicker
                  disabledDays={!bookingDate ? () => true : { after: new Date(bookingDate) }}
                  className="w-full"
                  onChange={(v) => {
                    if (v) {
                      const formattedDate = format(v, "yyyy-MM-dd");
                      const days = countDaysBeforeBooking(v);
                      logOfferYachtControlChange({
                        eventType: "offer_yacht.payment_schedule_changed",
                        context: scheduleProps.logContext,
                        field: `paymentSteps.${i}.dueDate`,
                        before: dates[i] ?? null,
                        after: formattedDate,
                        meta: { step_index: i },
                      });
                      form.setValue(`paymentSteps.${i}.days`, days);
                      if (formattedDate === bookingDate) {
                        form.setValue(`paymentSteps.${i}.type`, "on confirmation");
                      } else {
                        if (paymentSchedule[i].type === "on confirmation") {
                          form.setValue(`paymentSteps.${i}.type`, "before booking date");
                        }
                      }
                      return v;
                    } else {
                      const tempDates = [...dates];
                      logOfferYachtControlChange({
                        eventType: "offer_yacht.payment_schedule_changed",
                        context: scheduleProps.logContext,
                        field: `paymentSteps.${i}.dueDate`,
                        before: dates[i] ?? null,
                        after: "",
                        meta: { step_index: i },
                      });
                      tempDates[i] = "";
                      setDates(tempDates);
                      return "";
                    }
                  }}
                  value={dates[i] ? normalizeDate(dates[i]) : undefined}
                />
              </div>
              <FormField<OfferYachtForm, `paymentSteps.${number}.days`>
                name={`paymentSteps.${i}.days`}
                render={({ field: { value, onChange, ...props } }) => (
                  <FormItem className="flex-[1]" required>
                    <FormLabel>Days</FormLabel>
                    <FormControl>
                      <Input
                        {...props}
                        value={step.days}
                        disabled={step.type === "on confirmation"}
                        onFocus={() => captureFieldFocus(`paymentSteps.${i}.days`, step.days)}
                        onChange={(e) => {
                          const value = +e.target.value;
                          onChange(value);
                        }}
                        onBlur={(e) => {
                          props.onBlur?.();
                          logOfferYachtControlBlur({
                            eventType: "offer_yacht.payment_schedule_changed",
                            context: scheduleProps.logContext,
                            key: `paymentSteps.${i}.days`,
                            field: `paymentSteps.${i}.days`,
                            before: step.days,
                            after: form.getValues(`paymentSteps.${i}.days`),
                            meta: { step_index: i },
                          });
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<OfferYachtForm, `paymentSteps.${number}.type`>
                name={`paymentSteps.${i}.type`}
                render={({ field: { value, onChange } }) => (
                  <FormItem className="flex-[2]" required>
                    <FormLabel className="flex align-center">
                      Payment time
                      <InfoTooltip text={getDateTooltipText(i)} />
                    </FormLabel>
                    <div className="flex gap-2 items-end w-full">
                      <Select
                        onValueChange={(v) => {
                          logOfferYachtControlChange({
                            eventType: "offer_yacht.payment_schedule_changed",
                            context: scheduleProps.logContext,
                            field: `paymentSteps.${i}.type`,
                            before: step.type,
                            after: v,
                            meta: { step_index: i },
                          });
                          onChange(v);
                          if (v === "on confirmation") {
                            form.setValue(`paymentSteps.${i}.days`, 0);
                          }
                        }}
                        value={step.type}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="on confirmation">on confirmation</SelectItem>
                          <SelectItem value="after confirmation">after confirmation</SelectItem>
                          <SelectItem value="before booking date">before charter date</SelectItem>
                        </SelectContent>
                      </Select>
                      {paymentSchedule.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            logOfferYachtControlChange({
                              eventType: "offer_yacht.payment_schedule_changed",
                              context: scheduleProps.logContext,
                              field: "paymentSteps.removed",
                              before: paymentSchedule,
                              after: paymentSchedule.filter((_, idx) => idx !== i),
                              meta: { step_index: i },
                            });
                            form.setValue(
                              `paymentSteps`,
                              paymentSchedule.filter((_, idx) => idx !== i),
                            );
                          }}
                        >
                          <TrashIcon size={16} />
                        </Button>
                      )}
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </Fragment>
        ))}
        {form.formState.errors.paymentSteps?.root?.message && (
          <span className="text-sm text-destructive">{form.formState.errors.paymentSteps?.root?.message}</span>
        )}
        <Button
          onClick={() => {
            const nextPaymentSchedule = [
              ...paymentSchedule,
              {
                amount: null,
                operatorAmount: 0,
                originalPercentage: 0,
                days: null,
                scheduleStep: paymentSchedule?.length,
                isNewStep: true,
                isNewOffer: true,
              } as any,
            ];
            logOfferYachtControlChange({
              eventType: "offer_yacht.payment_schedule_changed",
              context: scheduleProps.logContext,
              field: "paymentSteps.added",
              before: paymentSchedule,
              after: nextPaymentSchedule,
              meta: { step_index: paymentSchedule?.length },
            });
            form.setValue(
              `paymentSteps`,
              nextPaymentSchedule,
              {
                shouldDirty: true,
              },
            );
          }}
          className="w-full mt-4"
          variant="secondary"
          type="button"
        >
          <PlusIcon size={16} />
          Add payment schedule step
        </Button>
      </div>
      <Separator className="mb-6" />
      <Pricing {...scheduleProps} />
    </div>
  );
}

const InfoTooltip = ({ text }: { text: string }) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="ml-2" size={16} />
        </TooltipTrigger>
        <TooltipContent>
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const renderPrice = (price?: number) => {
  if (!price) return null;
  return price.toFixed(2);
};
